import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import CookieBanner from './components/CookieBanner';
import { trackPageView } from './utils/metaPixel';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Courses from './pages/Courses';
import CourseLanding from './pages/CourseLanding';
import Dashboard from './pages/Dashboard';
import CoursePlayer from './pages/CoursePlayer';
import Admin from './pages/Admin';
import VisualEditor from './pages/VisualEditor';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import MagicAccess from './pages/MagicAccess';
import CheckoutSuccess from './pages/CheckoutSuccess';
import RequestAccess from './pages/RequestAccess';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ResendVerification from './pages/ResendVerification';
import BlueprintLanding from './pages/BlueprintLanding';
import BlueprintThankYou from './pages/BlueprintThankYou';
import ParentsHandbookLanding from './pages/ParentsHandbookLanding';
import ParentsHandbookThankYou from './pages/ParentsHandbookThankYou';
import ScamSmartLanding from './pages/ScamSmartLanding';
import ScamSmartThankYou from './pages/ScamSmartThankYou';
import ScamSmartDashboard from './pages/ScamSmartDashboard';
import ScamSmartTest from './pages/ScamSmartTest';
import ScamSmartRadar from './pages/ScamSmartRadar';
import ScamSmartSafeWord from './pages/ScamSmartSafeWord';
import ScamSmartFamily from './pages/ScamSmartFamily';
import ScamSmartLibrary from './pages/ScamSmartLibrary';
import ScamSmartTrusted from './pages/ScamSmartTrusted';
import ScamSmartUnlock from './pages/ScamSmartUnlock';
import ScamSmartPractice from './pages/ScamSmartPractice';
import AdBanners from './pages/AdBanners';
import BusinessAICyberSupport from './pages/BusinessAICyberSupport';
import PerthPropertyPulse from './pages/PerthPropertyPulse';
import PerthPropertyArticle from './pages/PerthPropertyArticle';
import { MockupA, MockupB, MockupC } from './pages/MockupPreviews';
import ScamSmartGuard from './components/ScamSmartGuard';

// Homepage Section Routes (for SEO - renders same content, scrolls to section)
import {
  FirstStepSection,
  BaselineSection,
  YourStackSection,
  ToolsSection,
  TestimonialsSection,
  SafetyCheckSection,
  SocialSection,
  ContactSection
} from './pages/HomeSectionWrapper';

// Legal Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import RefundPolicy from './pages/RefundPolicy';
import CookiePolicy from './pages/CookiePolicy';
import SecurityStatement from './pages/SecurityStatement';

import './App.css';

// Fires a Meta Pixel PageView on every client-side route change.
// The pixel base library is loaded in index.html but does NOT auto-track PageView
// (intentional, so SPA navigation can be measured accurately here).
const RouteTracker = () => {
  const location = useLocation();
  React.useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <RouteTracker />
        <div className="App">
          <Routes>
            <Route path="/" element={<Home />} />
            
            {/* SEO Section Routes - render homepage, scroll to section */}
            <Route path="/first-step" element={<FirstStepSection />} />
            <Route path="/baseline" element={<BaselineSection />} />
            <Route path="/your-stack" element={<YourStackSection />} />
            <Route path="/tools" element={<ToolsSection />} />
            <Route path="/testimonials" element={<TestimonialsSection />} />
            <Route path="/safety-check" element={<SafetyCheckSection />} />
            <Route path="/social" element={<SocialSection />} />
            <Route path="/contact" element={<ContactSection />} />
            
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/courses/:slug" element={<CourseLanding />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            
            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/security-statement" element={<SecurityStatement />} />
            
            {/* Guest checkout flow routes */}
            <Route path="/access/:token" element={<MagicAccess />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/request-access" element={<RequestAccess />} />
            
            {/* Blueprint landing page (hidden, no nav) */}
            <Route path="/course-1-blueprint" element={<BlueprintLanding />} />
            <Route path="/course-1-blueprint/thank-you" element={<BlueprintThankYou />} />
            
            {/* Parents' Digital Safety Handbook */}
            <Route path="/parents-handbook" element={<ParentsHandbookLanding />} />
            <Route path="/parents-handbook/thank-you" element={<ParentsHandbookThankYou />} />

            {/* ScamSmart by All Secured (Phase 1: public landing + 3 free scenarios + paywall) */}
            <Route path="/scamsmart" element={<ScamSmartLanding />} />
            <Route path="/scamsmart/thank-you" element={<ScamSmartThankYou />} />

            {/* ScamSmart paid dashboard + tools (Phase 2). Gated via ScamSmartGuard
                using the buyer email stored in localStorage after a successful purchase. */}
            <Route path="/scamsmart/dashboard" element={<ScamSmartGuard><ScamSmartDashboard /></ScamSmartGuard>} />
            <Route path="/scamsmart/test" element={<ScamSmartGuard><ScamSmartTest /></ScamSmartGuard>} />
            <Route path="/scamsmart/radar" element={<ScamSmartGuard><ScamSmartRadar /></ScamSmartGuard>} />
            <Route path="/scamsmart/safe-word" element={<ScamSmartGuard><ScamSmartSafeWord /></ScamSmartGuard>} />
            <Route path="/scamsmart/family" element={<ScamSmartGuard><ScamSmartFamily /></ScamSmartGuard>} />
            <Route path="/scamsmart/library" element={<ScamSmartGuard><ScamSmartLibrary /></ScamSmartGuard>} />
            <Route path="/scamsmart/practice" element={<ScamSmartGuard><ScamSmartPractice /></ScamSmartGuard>} />
            <Route path="/scamsmart/practice/:scenarioId" element={<ScamSmartGuard><ScamSmartPractice /></ScamSmartGuard>} />
            <Route path="/internal/ad-banners" element={<AdBanners />} />

            {/* Standalone business-facing landing page (for outreach / job applications) */}
            <Route path="/business-ai-cyber-support" element={<BusinessAICyberSupport />} />

            {/* Perth Property Pulse — property intelligence portal (All Secured initiative) */}
            <Route path="/perth-property-pulse" element={<PerthPropertyPulse />} />
            <Route path="/perth-property-pulse/articles/:slug" element={<PerthPropertyArticle />} />

            {/* Hidden visual mockups — three design directions for review */}
            <Route path="/preview/mockup-a" element={<MockupA />} />
            <Route path="/preview/mockup-b" element={<MockupB />} />
            <Route path="/preview/mockup-c" element={<MockupC />} />
            <Route path="/scamsmart/trusted" element={<ScamSmartGuard><ScamSmartTrusted /></ScamSmartGuard>} />

            {/* Creator / owner instant-access bookmark — token-gated */}
            <Route path="/scamsmart/unlock" element={<ScamSmartUnlock />} />
            
            {/* Email verification and password reset routes */}
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
            
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/my-courses/:slug" 
              element={
                <ProtectedRoute>
                  <CoursePlayer />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/course/:slug" 
              element={
                <ProtectedRoute>
                  <CoursePlayer />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/visual-editor" 
              element={
                <ProtectedRoute>
                  <VisualEditor />
                </ProtectedRoute>
              } 
            />
          </Routes>
          <CookieBanner />
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
